import type {
  AiAssistantDocumentContext,
  AiAssistantPageContext,
} from '@asset-metrix/portal-ui';

import { getActiveDynamicItemIndex } from '../navigation/activeItems';

export const ADDITIONAL_CONTEXT_HEADER = 'additional context:';
export const ADDITIONAL_DOCUMENTS_HEADER = 'additional documents:';

const ADDITIONAL_CONTEXT_MARKER = `\n\n${ADDITIONAL_CONTEXT_HEADER}\n`;
const ADDITIONAL_DOCUMENTS_MARKER = `\n\n${ADDITIONAL_DOCUMENTS_HEADER}\n`;

export type NavTreeItem = {
  completeSlug: string;
  label: string;
  items?: NavTreeItem[];
};

export type DynamicNavItem = {
  completeSlug: string;
  label: string;
  sublabels?: string[];
};

export function joinPageTitleTree(parts: Array<string | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('/');
}

function readablePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).replace(/[-_]+/g, ' ');
  } catch {
    return segment.replace(/[-_]+/g, ' ');
  }
}

export function pageSlugFromPathname(pathname: string): string {
  const match = pathname.match(/^\/(?:p|dp)\/[^/]+\/(.*)$/);
  return match?.[1] ?? pathname.replace(/^\//, '');
}

export function pageLabelFromHref(href: string): string {
  const [path] = href.split('?');
  const slug = pageSlugFromPathname(path);
  if (!slug) {
    return href;
  }

  return joinPageTitleTree(slug.split('/').map(readablePathSegment));
}

export function collectNavTitleTree(
  items: NavTreeItem[],
  searchSlug: string
): string[] {
  for (const item of items) {
    if (item.completeSlug === searchSlug) {
      return [item.label];
    }

    if (item.items && searchSlug.startsWith(`${item.completeSlug}/`)) {
      const rest = collectNavTitleTree(item.items, searchSlug);
      if (rest.length > 0) {
        return [item.label, ...rest];
      }
    }
  }

  return [];
}

export function resolveCurrentPage({
  pathname,
  search,
  slug,
  navTree,
  dynamicNavItems,
}: {
  pathname: string;
  search: string;
  slug?: string;
  navTree: NavTreeItem[];
  dynamicNavItems: DynamicNavItem[];
}): AiAssistantPageContext {
  const href = `${pathname}${search}`;
  const dynamicIndex = getActiveDynamicItemIndex(
    dynamicNavItems,
    pathname,
    search
  );
  if (dynamicIndex != null) {
    const item = dynamicNavItems[dynamicIndex];
    const label =
      joinPageTitleTree([item.label, ...(item.sublabels ?? [])]) ||
      pageLabelFromHref(href);
    return { href, label };
  }

  const pageSlug = slug || pageSlugFromPathname(pathname);
  const titleTree = pageSlug ? collectNavTitleTree(navTree, pageSlug) : [];
  if (titleTree.length > 0) {
    return { href, label: joinPageTitleTree(titleTree) };
  }

  return { href, label: pageLabelFromHref(href) };
}

function formatDocumentContextLine(
  document: Pick<AiAssistantDocumentContext, 'id' | 'label'>
): string {
  const label = document.label?.trim();
  return label && label !== document.id
    ? `${document.id} ${label}`
    : document.id;
}

function parseDocumentContextLine(line: string): AiAssistantDocumentContext {
  const space = line.search(/\s/);
  if (space === -1) {
    return { id: line, label: line };
  }

  const id = line.slice(0, space);
  const label = line.slice(space).trim() || id;
  return { id, label };
}

export function buildPrompt(
  userText: string,
  pageContexts: Pick<AiAssistantPageContext, 'href'>[] = [],
  documentContexts: Pick<AiAssistantDocumentContext, 'id' | 'label'>[] = []
): string {
  let prompt = userText;
  if (pageContexts.length > 0) {
    prompt += `${ADDITIONAL_CONTEXT_MARKER}${pageContexts
      .map((page) => page.href)
      .join('\n')}`;
  }
  if (documentContexts.length > 0) {
    prompt += `${ADDITIONAL_DOCUMENTS_MARKER}${documentContexts
      .map(formatDocumentContextLine)
      .join('\n')}`;
  }
  return prompt;
}

function takeMarkedLines(
  content: string,
  marker: string
): { rest: string; lines: string[] } {
  const index = content.lastIndexOf(marker);
  if (index === -1) {
    return { rest: content, lines: [] };
  }

  const lines = content
    .slice(index + marker.length)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return {
    rest: content.slice(0, index),
    lines,
  };
}

export function splitUserContent(content: string): {
  text: string;
  pageContexts?: AiAssistantPageContext[];
  documentContexts?: AiAssistantDocumentContext[];
} {
  const documents = takeMarkedLines(content, ADDITIONAL_DOCUMENTS_MARKER);
  const pages = takeMarkedLines(documents.rest, ADDITIONAL_CONTEXT_MARKER);
  const documentContexts =
    documents.lines.length > 0
      ? documents.lines.map(parseDocumentContextLine)
      : undefined;
  const pageContexts =
    pages.lines.length > 0
      ? pages.lines.map((href) => ({
          href,
          label: pageLabelFromHref(href),
        }))
      : undefined;

  if (documentContexts == null && pageContexts == null) {
    return { text: content };
  }

  return {
    text: pages.rest,
    ...(pageContexts ? { pageContexts } : {}),
    ...(documentContexts ? { documentContexts } : {}),
  };
}
